import {
  Component, EventEmitter, Input, OnDestroy, OnInit, Output,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ProjectsService } from '../../../../../core/projects.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { PunchlistSettingsFormComponent } from './punchlist-settings-form.component';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-punchlist-settings',
  templateUrl: './punchlist-settings.component.html',
  styleUrls: ['./punchlist-settings.component.scss'],
  standalone: false,
})
export class PunchlistSettingsComponent implements OnInit, OnDestroy {
  @Input() projectId: string;
  @Input() punchlists: any[] = [];
  @Output() changed = new EventEmitter<void>();
  @Output() selectPunchlist = new EventEmitter<any>();

  selectedRow: any = null;
  displayedColumns = ['name', 'status', 'start_date', 'completion_date', 'substantial_completion', 'prime_contractor', 'created_at', 'actions'];

  statusMap: Record<string, { label: string; textColor: string }> = {
    active: { label: 'Active', textColor: 'txt-approved' },
    closed: { label: 'Closed', textColor: 'txt-draft' },
  };

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private appHttpService: AppHttpService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    // After a punchlist is created/updated/deleted from the right panel, refresh the list
    this.projectsService.punchlistChanged
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.changed.emit());

    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => (this.selectedRow = null));
  }

  createPunchlist(): void {
    this.selectedRow = null;
    this.projectsService.openRightPanel.next({
      component: PunchlistSettingsFormComponent,
      data: { projectId: this.projectId },
    });
  }

  rowClicked(row: any): void {
    this.selectedRow = row;
    this.projectsService.openRightPanel.next({
      component: PunchlistSettingsFormComponent,
      data: { ...row, projectId: this.projectId },
    });
  }

  goToItems(row: any, event: Event): void {
    event.stopPropagation();
    this.selectPunchlist.emit(row);
  }

  // ---------- Exports + bulk-send ----------

  private safeName(row: any): string {
    return String(row?.name || 'punch list').replace(/[^A-Za-z0-9_-]+/g, '_');
  }

  downloadPdf(row: any): void {
    const id = row.uuid || row.id;
    this.appHttpService.getService(
      `/v1/punchlist/project/${this.projectId}/punchlist/${id}/pdf`,
      {},
      { responseType: 'text' },
    ).subscribe({
      next: (html: string) => {
        const w = window.open('', '_blank');
        if (w) { w.document.write(html); w.document.close(); }
      },
    });
  }

  downloadCsv(row: any): void {
    const id = row.uuid || row.id;
    this.appHttpService.getService(
      `/v1/punchlist/project/${this.projectId}/punchlist/${id}/csv`,
      {},
      { responseType: 'text' },
    ).subscribe({
      next: (csv: string) => {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `${this.safeName(row)}.csv`);
      },
    });
  }

  downloadKmz(row: any): void {
    const id = row.uuid || row.id;
    this.appHttpService.getService(
      `/v1/punchlist/project/${this.projectId}/punchlist/${id}/kmz`,
      {},
      { responseType: 'blob' },
    ).subscribe({
      next: (resp: Blob) => saveAs(resp, `${this.safeName(row)}.kmz`),
    });
  }

  sendAllToContractor(row: any): void {
    const dialogData = new ConfirmDialogModel(
      'Forward all items to contractor?',
      `All open items in <b>${row?.name || 'this punch list'}</b> will be forwarded to the contractor and notification users will be emailed.`,
    );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result) return;
      const id = row.uuid || row.id;
      this.appHttpService.postService(
        `/v1/punchlist/project/${this.projectId}/punchlist/${id}/send-all`,
        {},
      ).subscribe({
        next: (resp: any) => {
          this.commonService.notification(resp?.message || 'Items forwarded', 'success');
          this.projectsService.punchlistChanged.next();
        },
        error: (err: any) => {
          this.commonService.errorNotification(err?.error?.error || 'Failed to forward items');
        },
      });
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
